import { useEffect } from 'react';
import {
  fetchAuthSession,
  fetchUserAttributes,
  updateUserAttributes,
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  type SignInInput,
} from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { useStore } from '@/store';
import { UPDATE_BOOKING_SESSION } from '@/lib/graphql/mutations';
import { GET_MY_PROFILE } from '@/lib/graphql/queries';

// Lazy init — generateClient() must be called after Amplify.configure()
let _client: { graphql: Function } | null = null;
const getClient = () => {
  if (!_client) _client = generateClient();
  return _client;
};

// Module-level guard so the (sessionId, sub) link mutation fires at most once
// app-wide, even though useAuth() is called from several components.
let _linkedKey: string | null = null;

function buildFullName(given?: string, family?: string, email?: string): string {
  const parts = [given, family].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : (email ?? '');
}

export function useAuth() {
  const { setAuth, clearAuth, sessionId } = useStore();
  const cognitoSub = useStore((s) => s.cognitoSub);

  // On mount: restore authenticated session if one exists (e.g., after page refresh)
  useEffect(() => {
    fetchUserAttributes()
      .then(async (attrs) => {
        setAuth({
          isAuthenticated: true,
          userEmail: attrs.email ?? null,
          userName: buildFullName(attrs.given_name, attrs.family_name, attrs.email),
          cognitoSub: attrs.sub ?? null,
          photoUrl: (attrs.picture as string | undefined) ?? null,
        });
        // Check admin group membership
        try {
          const session = await fetchAuthSession();
          const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];
          setAuth({ isAdmin: groups.includes('admins') });
        } catch { /* non-fatal */ }
      })
      .catch(() => {/* guest */});
  }, []);

  // Link the booking session to the Cognito User Pool sub once both are available.
  // createSession stores `cognitoIdentityId` as userId by default (Amplify's default
  // identityPool auth mode), but confirmBooking indexes BOOKING# records by
  // USER#<sub> for listMyBookings to find them. Without this linking, bookings
  // made after a page reload never appear in My Flights.
  useEffect(() => {
    if (!sessionId || !cognitoSub) return;
    const key = `${sessionId}:${cognitoSub}`;
    if (_linkedKey === key) return;
    _linkedKey = key;
    (getClient().graphql({
      query: UPDATE_BOOKING_SESSION,
      variables: { sessionId, patch: { userId: cognitoSub } },
    }) as Promise<unknown>).catch((err) => {
      console.warn('[useAuth] Session link failed:', err);
      if (_linkedKey === key) {
        _linkedKey = null;
      }
    });
  }, [sessionId, cognitoSub]);

  async function handleSignIn(email: string, password: string): Promise<void> {
    await signIn({ username: email, password } as SignInInput);

    const attrs = await fetchUserAttributes();
    const sub = attrs.sub ?? '';
    const userEmail = attrs.email ?? '';
    const userName = buildFullName(attrs.given_name, attrs.family_name, userEmail);

    setAuth({ isAuthenticated: true, userEmail, userName, cognitoSub: sub, photoUrl: (attrs.picture as string | undefined) ?? null });

    // Detect admin group
    try {
      const session = await fetchAuthSession();
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined) ?? [];
      setAuth({ isAdmin: groups.includes('admins') });
    } catch { /* non-fatal */ }

    // Fetch loyalty data from DDB
    try {
      const profile = await (getClient().graphql({ query: GET_MY_PROFILE, authMode: 'userPool' }) as Promise<{ data: { getMyProfile: { loyaltyId?: string; loyaltyTier?: string; milesBalance?: number; firstName?: string; lastName?: string; wizardCompleted?: boolean; birthDate?: string; passportNumber?: string; prefSeatLocation?: string; prefCabinClass?: string; prefMealType?: string; prefHomeAirport?: string; prefTravelType?: string; prefLanguage?: string; addressLine1?: string; addressCity?: string; addressState?: string; addressZip?: string; addressCountry?: string } | null } }>);
      const p = profile?.data?.getMyProfile;
      if (p) {
        setAuth({
          ...(p.firstName && p.lastName ? { userName: `${p.firstName} ${p.lastName}`.trim() } : {}),
          loyaltyTier: p.loyaltyTier ?? undefined,
          milesBalance: p.milesBalance ?? undefined,
          frequentFlyerNumber: p.loyaltyId ?? undefined,
          wizardCompleted:     p.wizardCompleted ?? null,
          birthDate:           p.birthDate ?? null,
          passportNumber:      p.passportNumber ?? null,
          prefSeatLocation:    p.prefSeatLocation ?? null,
          prefCabinClass:      p.prefCabinClass ?? null,
          prefMealType:        p.prefMealType ?? null,
          prefHomeAirport:     p.prefHomeAirport ?? null,
          prefTravelType:      p.prefTravelType ?? null,
          prefLanguage:        p.prefLanguage ?? null,
          addressLine1:        p.addressLine1 ?? null,
          addressCity:         p.addressCity ?? null,
          addressState:        p.addressState ?? null,
          addressZip:          p.addressZip ?? null,
          addressCountry:      p.addressCountry ?? null,
        });
      }
    } catch (err) {
      console.warn('[useAuth] getMyProfile failed:', err);
    }

    // Link the existing guest session to the authenticated Cognito sub
    const currentSessionId = useStore.getState().sessionId;
    if (currentSessionId && sub) {
      try {
        await (getClient().graphql({
          query: UPDATE_BOOKING_SESSION,
          variables: { sessionId: currentSessionId, patch: { userId: sub } },
        }) as Promise<unknown>);
      } catch (err) {
        console.warn('[useAuth] Session upgrade failed:', err);
      }
    }
  }

  async function handleSignOut(): Promise<void> {
    await signOut();
    _linkedKey = null;
    clearAuth();
  }

  async function handleSignUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone: string,
  ): Promise<{ nextStep: string }> {
    const result = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          given_name: firstName,
          family_name: lastName,
          phone_number: phone, // E.164 format e.g. +15551234567
        },
      },
    });
    return { nextStep: result.nextStep.signUpStep };
  }

  async function handleConfirmSignUp(email: string, code: string): Promise<void> {
    await confirmSignUp({ username: email, confirmationCode: code });
    // Post-confirmation Lambda fires automatically — Customer Profile is created
  }

  async function handleResendCode(email: string): Promise<void> {
    await resendSignUpCode({ username: email });
  }

  async function handleUpdateProfile(updates: {
    firstName?: string;
    lastName?: string;
    photoUrl?: string;
  }): Promise<void> {
    const attrs: Record<string, string> = {};
    if (updates.firstName !== undefined) attrs['given_name'] = updates.firstName;
    if (updates.lastName !== undefined) attrs['family_name'] = updates.lastName;
    if (updates.photoUrl !== undefined) attrs['picture'] = updates.photoUrl;

    await updateUserAttributes({ userAttributes: attrs });

    // Refresh store with new values
    const current = useStore.getState();
    const firstName = updates.firstName ?? current.userName?.split(' ')[0] ?? '';
    const lastName = updates.lastName ?? current.userName?.split(' ').slice(1).join(' ') ?? '';
    setAuth({
      userName: buildFullName(
        updates.firstName ?? undefined,
        updates.lastName ?? undefined,
        current.userEmail ?? undefined,
      ) || current.userName,
      ...(updates.photoUrl !== undefined ? { photoUrl: updates.photoUrl } : {}),
    });
    void firstName; void lastName; // used in closure
  }

  return {
    handleSignIn,
    handleSignOut,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
    handleUpdateProfile,
  };
}
